import { useDeptTreeList } from "@/hooks/services";
import { Empty, Radio, Spin, Tree } from "antd";
import { useModalBody } from "./hooks/useModalBody";
import styles from "./index.module.less";
import type { PersonnelItem, OperationEvent } from "./type";

// ==================== 类型定义 ====================
type ModalBodyProps = {
  selectedPersonnel?: PersonnelItem | null;
  setSelectedPersonnel?: (personnel: PersonnelItem | null) => void;
  searchedList: PersonnelItem[];
  eventType: OperationEvent;
  setEventType: (event: OperationEvent) => void;
  selectedDeptNo: string;
  setSelectedDeptNo: (deptNo: string) => void;
  isLoading?: boolean;
};

// ==================== 渲染函数 ====================
const renderPersonnelCard = (personnel: PersonnelItem) => (
  <div className={styles.personnelCard}>
    <Radio value={personnel.userId} className={styles.radio} />
    <span className={styles.name}>
      {personnel.nickName || personnel.userName}
    </span>
  </div>
);

// ==================== 主组件 ====================
export const ModalBody: React.FC<ModalBodyProps> = ({
  selectedPersonnel,
  setSelectedPersonnel,
  searchedList,
  eventType,
  setEventType,
  selectedDeptNo,
  setSelectedDeptNo,
  isLoading = false,
}) => {
  const { displayPersonnelList, selectedDeptName } = useModalBody({
    searchedList,
    eventType,
    selectedDeptNo,
  });

  const { deptTreeData } = useDeptTreeList();

  // 是否在搜索状态
  const isSearching = eventType === "search" && searchedList.length > 0;

  const onPersonnelChange = (e: any) => {
    const userId = e.target.value;
    const personnel =
      displayPersonnelList.find((p) => p.userId === userId) || null;
    setSelectedPersonnel?.(personnel);
  };

  const onDeptSelect = (_selectedKeys: React.Key[], info: any) => {
    // 获取选中部门的 key 作为 deptNo
    const deptNo = String(info.node.key);
    setSelectedDeptNo(deptNo);

    // 切换到部门选择状态
    setEventType("deptSelect");
    setSelectedPersonnel?.(null);
  };

  return (
    <div className={styles.panelContainer}>
      {/* 左侧部门树 - 始终显示 */}
      <div className={styles.leftPanel}>
        <Tree showLine={true} onSelect={onDeptSelect} treeData={deptTreeData} />
      </div>

      {/* 右侧人员列表 */}
      <div className={styles.rightPanel}>
        {/* 部门标题 - 当选择了部门时显示 */}
        {eventType === "deptSelect" && selectedDeptName && (
          <div className={styles.deptHeader}>
            <span className={styles.deptTitle}>{selectedDeptName}</span>
          </div>
        )}

        {/* 搜索结果标题 - 当在搜索状态且有结果时显示 */}
        {isSearching && (
          <div className={styles.deptHeader}>
            <span className={styles.deptTitle}>搜索结果</span>
          </div>
        )}

        {/* Loading 状态 */}
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spin tip="加载中..." />
          </div>
        ) : (
          <Radio.Group
            className={styles.personnelGroup}
            onChange={onPersonnelChange}
            value={selectedPersonnel?.userId}
          >
            {displayPersonnelList.map((personnel) => (
              <div key={personnel.userId}>{renderPersonnelCard(personnel)}</div>
            ))}
          </Radio.Group>
        )}

        {!isLoading && displayPersonnelList.length === 0 && (
          <Empty style={{ marginTop: 80 }} />
        )}
      </div>
    </div>
  );
};
